import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  fetchUserManagement,
  updateUser as updateUserService,
  deleteUser as deleteUserService,
  fetchAllRoles as fetchAllRolesService,
  fetchAllSuperRegions as fetchAllSuperRegionsService,
  fetchAllRegions as fetchAllRegionsService,
  fetchAllCountries as fetchAllCountriesService,
} from "../services/userManagement.service";
import { fetchAllPages } from "../../util/helpers";

const initialState = {
  rows: [],
  pageNumber: 1,
  pageSize: 10,
  totalRecords: 0,
  totalPages: 0,
  loading: false,
  error: null,

  saveLoading: false,
  saveError: null,

  deleteLoading: false,
  deleteError: null,

  roles: [],
  rolesLoading: false,

  superRegions: [],
  superRegionsLoading: false,

  regions: [],
  regionsLoading: false,

  countries: [],
  countriesLoading: false,

  nearshoreCountries: [],
  nearshoreCountriesLoading: false,
};

function mapUserRow(user) {
  return {
    id: String(user?.userManagementID ?? ""),
    email: user?.userEmail || "",
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    region: user?.region || "--",
    coe: user?.coe || "--",
    role: user?.role || "",
    isActive: String(user?.isActive || "")
      .toLowerCase()
      .trim() === "yes",
    isActiveRaw: user?.isActive || "Yes",
    superRegionIDs: user?.superRegionIDs || [],
    regionIDs: user?.regionIDs || [],
    countryIds: user?.countryIds || [],
    processingTeamType: user?.processingTeamType || "",
    processingTeamCountryIds: user?.processingTeamCountryIds || [],
  };
}

export const fetchUserManagementTable = createAsyncThunk(
  "userManagement/fetchTable",
  async (request = {}, { rejectWithValue }) => {
    try {
      return await fetchAllPages({
        fetchPage: ({ pageNumber, pageSize }) =>
          fetchUserManagement({ ...request, pageNumber, pageSize }),
        getRows: (res) => res?.data?.data?.data,
        getTotal: (res) => res?.data?.data?.totalRecords,
        withRows: (res, rows) => ({
          ...res,
          data: { ...res.data, data: { ...res.data.data, data: rows } },
        }),
      });
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  },
);

export const updateUserThunk = createAsyncThunk(
  "userManagement/updateUser",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await updateUserService(userData);
      return response.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  },
);

export const deleteUserThunk = createAsyncThunk(
  "userManagement/deleteUser",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await deleteUserService(payload);
      return response.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  },
);

export const fetchRolesThunk = createAsyncThunk(
  "userManagement/fetchRoles",
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchAllRolesService();
      return response.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  },
);

export const fetchSuperRegionsThunk = createAsyncThunk(
  "userManagement/fetchSuperRegions",
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchAllSuperRegionsService();
      return response.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  },
);

export const fetchRegionsThunk = createAsyncThunk(
  "userManagement/fetchRegions",
  async ({ superRegionIDs }, { rejectWithValue }) => {
    try {
      const response = await fetchAllRegionsService({ superRegionIDs });
      return response.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  },
);

export const fetchCountriesThunk = createAsyncThunk(
  "userManagement/fetchCountries",
  async ({ superRegionIDs, regionIDs }, { rejectWithValue }) => {
    try {
      const response = await fetchAllCountriesService({ superRegionIDs, regionIDs });
      return response.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  },
);
export const fetchNearshoreCountriesThunk = createAsyncThunk(
  "userManagement/fetchNearshoreCountries",
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchAllCountriesService({
        superRegionIDs: [0],
        regionIDs: [0],
      });

      return response.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  },
);

const userManagementSlice = createSlice({
  name: "userManagement",
  initialState,
  reducers: {
    resetSaveError: (state) => {
      state.saveError = null;
    },
    resetDeleteError: (state) => {
      state.deleteError = null;
    },
    clearRegions: (state) => {
      state.regions = [];
    },
    clearCountries: (state) => {
      state.countries = [];
    },
    clearNearshoreCountries: (state) => {
    state.nearshoreCountries = [];
    state.nearshoreCountriesLoading = false;
  },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchUserManagementTable.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserManagementTable.fulfilled, (state, action) => {
        state.loading = false;
        const pagedResponse = action.payload?.data?.data || {};
        const apiRows = pagedResponse?.data || [];

        state.pageNumber = pagedResponse?.pageNumber || 1;
        state.pageSize = pagedResponse?.pageSize || 10;
        state.totalRecords = pagedResponse?.totalRecords || 0;
        state.totalPages = pagedResponse?.totalPages || 0;
        state.rows = Array.isArray(apiRows) ? apiRows.map(mapUserRow) : [];
      })
      .addCase(fetchUserManagementTable.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(updateUserThunk.pending, (state) => {
        state.saveLoading = true;
        state.saveError = null;
      })
      .addCase(updateUserThunk.fulfilled, (state) => {
        state.saveLoading = false;
      })
      .addCase(updateUserThunk.rejected, (state, action) => {
        state.saveLoading = false;
        state.saveError = action.payload;
      });

    builder
      .addCase(deleteUserThunk.pending, (state) => {
        state.deleteLoading = true;
        state.deleteError = null;
      })
      .addCase(deleteUserThunk.fulfilled, (state, action) => {
        state.deleteLoading = false;
        const deletedId = String(action.meta?.arg?.userManagementID ?? "");
        if (deletedId) {
          state.rows = state.rows.filter((r) => r.id !== deletedId);
        }
      })
      .addCase(deleteUserThunk.rejected, (state, action) => {
        state.deleteLoading = false;
        state.deleteError = action.payload;
      });

    builder
      .addCase(fetchRolesThunk.pending, (state) => {
        state.rolesLoading = true;
      })
      .addCase(fetchRolesThunk.fulfilled, (state, action) => {
        state.rolesLoading = false;
        const data = action.payload?.data || action.payload;
        state.roles = Array.isArray(data) ? data : [];
      })
      .addCase(fetchRolesThunk.rejected, (state) => {
        state.rolesLoading = false;
      });

    builder
      .addCase(fetchSuperRegionsThunk.pending, (state) => {
        state.superRegionsLoading = true;
      })
      .addCase(fetchSuperRegionsThunk.fulfilled, (state, action) => {
        state.superRegionsLoading = false;
        const data = action.payload?.data || action.payload;
        state.superRegions = Array.isArray(data) ? data : [];
      })
      .addCase(fetchSuperRegionsThunk.rejected, (state) => {
        state.superRegionsLoading = false;
      });

    builder
      .addCase(fetchRegionsThunk.pending, (state) => {
        state.regionsLoading = true;
      })
      .addCase(fetchRegionsThunk.fulfilled, (state, action) => {
        state.regionsLoading = false;
        const data = action.payload?.data || action.payload;
        state.regions = Array.isArray(data) ? data : [];
      })
      .addCase(fetchRegionsThunk.rejected, (state) => {
        state.regionsLoading = false;
      });

    builder
      .addCase(fetchCountriesThunk.pending, (state) => {
        state.countriesLoading = true;
      })
      .addCase(fetchCountriesThunk.fulfilled, (state, action) => {
        state.countriesLoading = false;
        const data = action.payload?.data || action.payload;
        state.countries = Array.isArray(data) ? data : [];
      })
      .addCase(fetchCountriesThunk.rejected, (state) => {
        state.countriesLoading = false;
      });
      builder
  .addCase(fetchNearshoreCountriesThunk.pending, (state) => {
    state.nearshoreCountriesLoading = true;
  })
  .addCase(fetchNearshoreCountriesThunk.fulfilled, (state, action) => {
    state.nearshoreCountriesLoading = false;

    const data = action.payload?.data || action.payload;

    state.nearshoreCountries = Array.isArray(data)
      ? data
      : [];
  })
  .addCase(fetchNearshoreCountriesThunk.rejected, (state) => {
    state.nearshoreCountriesLoading = false;
  });
  },
});

export const {
  resetSaveError,
  resetDeleteError,
  clearRegions,
  clearCountries,
} = userManagementSlice.actions;

export default userManagementSlice.reducer;
